function esc(s) {
  return String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function fmtMoney(n, currency) {
  if (!n || Number(n) === 0) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(Number(n));
  } catch {
    return `${currency} ${Number(n).toLocaleString('en-US')}`;
  }
}

// Convert scope text to bullet list if it contains sentences separated by ". " or "\n"
function scopeToList(text) {
  if (!text) return '';
  // Split on newline or ". " followed by capital/dash
  const lines = text
    .split(/\n|(?<=\.)\s+(?=[A-ZÁÉÍÓÚÑ\-])/)
    .map(l => l.trim().replace(/\.$/, ''))
    .filter(Boolean);
  if (lines.length <= 1) return `<p style="margin:0;line-height:1.7;color:#374151;font-size:14px">${esc(text)}</p>`;
  return `<ul style="margin:0;padding:0;list-style:none">${
    lines.map(l => `<li style="padding:5px 0 5px 22px;position:relative;font-size:14px;color:#374151;line-height:1.55"><span style="position:absolute;left:0;color:#3D9E94;font-weight:700">✓</span>${esc(l)}</li>`).join('')
  }</ul>`;
}

export function buildProposalHTML({ accountName, currency, lines, conditions, snapshotAt, isDraft = false, lang = 'es' }) {
  const isEn = lang === 'en';

  const L = {
    heading:    isEn ? 'Service Proposal'          : 'Propuesta de servicios',
    prepared:   isEn ? 'Prepared for'              : 'Preparada para',
    scopeTitle: isEn ? 'Scope of services'         : 'Alcance de servicios',
    investment: isEn ? 'Investment'                : 'Inversión',
    oneTime:    isEn ? 'One-time payment'          : 'Pago único',
    monthly:    isEn ? '/ month'                   : '/ mes',
    totalTitle: isEn ? 'Total investment'          : 'Inversión total',
    termsTitle: isEn ? 'Terms & conditions'        : 'Condiciones comerciales',
    nextTitle:  isEn ? 'Next steps'                : 'Próximos pasos',
    nextBody:   isEn
      ? '1. Review this proposal\n2. Schedule a 20-minute call to confirm scope\n3. Sign the agreement\n4. Kick-off'
      : '1. Revisar esta propuesta\n2. Agendar una llamada de 20 minutos para confirmar el alcance\n3. Firmar el acuerdo\n4. Kick-off',
    footerLine1: 'Ale Cadario · Oike',
    footerLine2: 'ale@alecadario.com',
    draft:      isEn ? '⚠ DRAFT — pending approval' : '⚠ BORRADOR — pendiente de aprobación',
    custom:     isEn ? 'Custom pricing — contact Oike' : 'Precio personalizado — contactar a Oike',
  };

  const date = snapshotAt
    ? new Date(snapshotAt).toLocaleDateString(isEn ? 'en-US' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : new Date().toLocaleDateString(isEn ? 'en-US' : 'es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

  const validLines = (lines || []).filter(l => l.service?.trim());
  const totalOnce    = validLines.reduce((s, l) => s + (Number(l.onceFee)    || 0), 0);
  const totalMonthly = validLines.reduce((s, l) => s + (Number(l.monthlyFee) || 0), 0);

  const serviceCards = validLines.map(l => {
    const once    = fmtMoney(l.onceFee,    currency);
    const monthly = fmtMoney(l.monthlyFee, currency);
    const isCustom = !once && !monthly;

    const priceBlock = isCustom
      ? `<div style="margin-top:14px;padding:14px 18px;background:#f0f2f5;border-radius:6px;font-size:14px;color:#6b7280;font-weight:600">${L.custom}</div>`
      : `<div style="margin-top:14px;display:flex;gap:24px;flex-wrap:wrap">
          ${once    ? `<div><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#9ca3af;margin-bottom:4px">${L.oneTime}</div><div style="font-size:22px;font-weight:700;color:#12121F">${esc(once)}</div></div>` : ''}
          ${monthly ? `<div><div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#9ca3af;margin-bottom:4px">${L.investment}</div><div style="font-size:22px;font-weight:700;color:#3D9E94">${esc(monthly)} <span style="font-size:14px;font-weight:500;color:#6b7280">${L.monthly}</span></div></div>` : ''}
         </div>`;

    return `
    <div style="background:white;border:2px solid #e0e0e0;border-top:5px solid #3D9E94;border-radius:8px;padding:28px 30px;margin-bottom:20px;page-break-inside:avoid;break-inside:avoid">
      <h3 style="margin:0 0 10px;font-size:18px;font-weight:700;color:#12121F">${esc(l.service)}</h3>
      ${l.scope ? `<div style="margin-top:12px">${scopeToList(l.scope)}</div>` : ''}
      ${priceBlock}
    </div>`;
  }).join('');

  const totalRow = (totalOnce > 0 || totalMonthly > 0) ? `
  <div style="margin-top:8px;background:#e8f5f1;border:2px solid #3D9E94;border-radius:8px;padding:20px 28px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;page-break-inside:avoid;break-inside:avoid">
    <span style="font-size:15px;font-weight:700;color:#12121F">${L.totalTitle}</span>
    <div style="display:flex;gap:28px;flex-wrap:wrap">
      ${totalOnce    > 0 ? `<span style="font-size:22px;font-weight:700;color:#12121F">${esc(fmtMoney(totalOnce, currency))}</span>` : ''}
      ${totalMonthly > 0 ? `<span style="font-size:22px;font-weight:700;color:#3D9E94">${esc(fmtMoney(totalMonthly, currency))} <span style="font-size:14px;font-weight:500;color:#6b7280">${L.monthly}</span></span>` : ''}
    </div>
  </div>` : '';

  const termsBlock = conditions ? `
  <section style="margin-bottom:50px">
    <h2 style="color:#12121F;border-bottom:3px solid #3D9E94;padding-bottom:14px;margin-bottom:24px;font-size:22px">${L.termsTitle}</h2>
    <div style="background:#f9f9f9;padding:24px 28px;border-radius:6px;font-size:14px;color:#374151;line-height:1.8;white-space:pre-wrap">${esc(conditions)}</div>
  </section>` : '';

  const nextSteps = `
  <section style="margin-bottom:50px">
    <h2 style="color:#12121F;border-bottom:3px solid #3D9E94;padding-bottom:14px;margin-bottom:24px;font-size:22px">${L.nextTitle}</h2>
    <div style="background:#e8f5f1;padding:24px 28px;border-radius:8px;border:2px solid #3D9E94">
      <ol style="padding-left:20px;margin:0;line-height:2.2;font-size:15px;color:#12121F">
        ${L.nextBody.split('\n').map(s => `<li>${esc(s.replace(/^\d+\.\s*/,''))}</li>`).join('')}
      </ol>
    </div>
  </section>`;

  const draftBanner = isDraft
    ? `<div style="background:#fef3c7;border-bottom:2px solid #fde68a;padding:12px 40px;font-size:13px;font-weight:700;color:#92400e;letter-spacing:.3px;text-align:center">${L.draft}</div>`
    : '';

  return `<!doctype html>
<html lang="${isEn ? 'en' : 'es'}">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${isEn ? 'Proposal' : 'Propuesta'} — ${esc(accountName)}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;line-height:1.6;color:#333;background:#f5f5f5;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    h1,h2,h3{margin:0}
    .container{max-width:900px;margin:0 auto;background:white;box-shadow:0 0 20px rgba(0,0,0,.1)}
    @media print{
      body{background:#fff}
      .container{max-width:100%;box-shadow:none}
      section{page-break-inside:avoid}
    }
    @media(max-width:640px){
      header{padding:40px 24px !important}
      .content{padding:30px 24px !important}
      footer{padding:24px !important}
    }
  </style>
</head>
<body>
<div class="container">

  ${draftBanner}

  <header style="background:linear-gradient(135deg,#12121F 0%,#3D9E94 100%);color:white;padding:56px 40px;text-align:center">
    <div style="font-size:12px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,0.55);margin-bottom:10px">Oike · ${esc(L.heading)}</div>
    <div style="font-size:13px;color:rgba(255,255,255,0.5);margin-bottom:6px">${L.prepared}</div>
    <h1 style="font-size:2.4em;font-weight:700;line-height:1.1;color:#fff">${esc(accountName)}</h1>
    <p style="margin-top:12px;font-size:14px;color:rgba(255,255,255,0.5)">${date}</p>
  </header>

  <div class="content" style="padding:40px">

    <section style="margin-bottom:50px">
      <h2 style="color:#12121F;border-bottom:3px solid #3D9E94;padding-bottom:14px;margin-bottom:28px;font-size:22px">${L.scopeTitle}</h2>
      ${serviceCards}
      ${totalRow}
    </section>

    ${termsBlock}
    ${nextSteps}

  </div>

  <footer style="background:#12121F;color:white;padding:28px 40px;text-align:center">
    <p style="font-weight:700;font-size:15px;margin-bottom:4px">${L.footerLine1}</p>
    <p style="font-size:13px;color:rgba(255,255,255,0.55)">${L.footerLine2}</p>
    <p style="margin-top:12px;font-size:12px;color:rgba(255,255,255,0.35)">${esc(accountName)} · ${date}</p>
  </footer>

</div>
</body>
</html>`;
}

export function printProposalPDF({ accountName, currency, lines, conditions, lang = 'es', isDraft = false }) {
  const html = buildProposalHTML({ accountName, currency, lines, conditions, lang, isDraft, snapshotAt: new Date().toISOString() });
  const win = window.open('', '_blank');
  if (!win) { alert('Pop-up bloqueado — permitir pop-ups para esta página e intentar de nuevo.'); return; }
  win.document.write(html);
  win.document.close();
  win.addEventListener('load', () => setTimeout(() => win.print(), 250), { once: true });
  setTimeout(() => { try { win.print(); } catch {} }, 900);
}
